import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { login as apiLogin, logout as apiLogout } from '../api/auth';
import { getMyProfile } from '../api/users';
import { setSessionExpiredHandler } from '../api/axiosInstance';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));
  const [profile, setProfile] = useState(null);

  const isLoggedIn = Boolean(accessToken);

  // 토큰 갱신에 실패하면 axios가 여기로 알려준다.
  // 상태를 내려야 헤더·저장 목록 등이 즉시 로그아웃 상태로 따라온다.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAccessToken(null);
      setProfile(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  // 토큰이 생기면 프로필을 받아오고, 로그아웃하면 비운다.
  // 실패해도 조용히 넘어간다 — 헤더에 프로필이 안 뜰 뿐 로그인 상태는 유지된다.
  useEffect(() => {
    if (!accessToken) {
      setProfile(null);
      return undefined;
    }
    let cancelled = false;
    getMyProfile()
      .then((data) => { if (!cancelled) setProfile(data ?? null); })
      .catch(() => { if (!cancelled) setProfile(null); });
    return () => { cancelled = true; };
  }, [accessToken]);

  const login = useCallback(async (email, password) => {
    const { accessToken: at, refreshToken: rt } = await apiLogin(email, password);
    if (at) {
      localStorage.setItem('accessToken', at);
      setAccessToken(at);
    }
    if (rt) {
      localStorage.setItem('refreshToken', rt);
    }
  }, []);

  const logout = useCallback(async () => {
    const rt = localStorage.getItem('refreshToken');
    try {
      if (rt) await apiLogout(rt);
    } catch {
      // ignore logout errors
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setAccessToken(null);
      setProfile(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, accessToken, profile, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
